import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, FilePdf, HandPalm, SealCheck } from '@phosphor-icons/react'
import { useAuth } from '@/auth/context'
import { canOverrideTwoPerson } from '@/auth/roles'
import { TextArea } from '@/ui'
import { errorMessage } from '../../api/core'
import { usePackLot, useRecallExport, type PackLotDetail } from '../../api/pack'
import {
  useChecklist,
  useLabReport,
  useReleaseDecision,
  useReleaseQueue,
  type QueueRow,
  type QueueState,
} from '../../api/quality'
import { ActionButton } from '../../components/Can'
import { QueryState } from '../../components/Layout'
import {
  BoardColumns,
  BoardPage,
  BoardTable,
  Counter,
  Counters,
  LinkButton,
  Note,
  PanelActions,
  Pill,
  SidePanel,
  TwoLine,
  type BoardColumn,
} from '../../components/board'
import { useUrlState } from '../../lib/useUrlState'
import { boardDate, boardWhen } from '../../model/payoutBoard'
import { kg2 } from '../../model/packing'
import {
  linesNotGreen,
  releaseLines,
  signBlocked,
  HOLD_REASON_MIN,
  type BoardLine,
} from '../../model/release'
import { PackLotPill } from '../packing/shared'
import { Signer, SignFields } from './shared'

/** Release: pack lots waiting for a named quality person, on hold, or released (F7.6-d). */
export function ReleaseQueuePage() {
  const [f, setF] = useUrlState({ state: 'WAITING', page: 1 })
  const state = f.state as QueueState
  const list = useReleaseQueue({ state, page: f.page })
  const waiting = useReleaseQueue({ state: 'WAITING', limit: 1 })
  const held = useReleaseQueue({ state: 'HELD', limit: 1 })
  const released = useReleaseQueue({ state: 'RELEASED', limit: 1 })
  const navigate = useNavigate()
  const columns: BoardColumn<QueueRow>[] = [
    {
      id: 'code',
      header: 'Pack lot',
      cell: (r) => (
        <TwoLine top={r.code} sub={r.packDate ? `packed ${boardDate(r.packDate)}` : undefined} />
      ),
    },
    { id: 'product', header: 'Product', cell: (r) => <TwoLine top={r.skuName} sub={r.skuCode} /> },
    { id: 'pouches', header: 'Pouches', align: 'right', cell: (r) => r.pouchCount ?? '—' },
    { id: 'state', header: 'State', cell: (r) => <PackLotPill status={r.status} hold={r.hold} /> },
    {
      id: 'last',
      header: 'Last decision',
      hideBelow: 'md',
      cell: (r) =>
        r.latestDecision ? (
          <TwoLine
            top={`${r.latestDecision.auto ? 'Automatic ' : ''}${r.latestDecision.decision.toLowerCase()}`}
            sub={`${boardWhen(r.latestDecision.decidedAt)}${r.latestDecision.reason ? ` · ${r.latestDecision.reason}` : ''}`}
          />
        ) : (
          '—'
        ),
    },
  ]
  return (
    <BoardPage title="Release">
      <Counters>
        <Counter
          label="Waiting for release"
          value={waiting.data?.total ?? 0}
          loading={waiting.isLoading}
          active={state === 'WAITING'}
          onClick={() => setF({ state: 'WAITING' })}
        />
        <Counter
          label="On hold"
          value={held.data?.total ?? 0}
          loading={held.isLoading}
          active={state === 'HELD'}
          onClick={() => setF({ state: 'HELD' })}
        />
        <Counter
          label="Released"
          value={released.data?.total ?? 0}
          loading={released.isLoading}
          active={state === 'RELEASED'}
          onClick={() => setF({ state: 'RELEASED' })}
        />
        <Counter
          label="All"
          value={(waiting.data?.total ?? 0) + (held.data?.total ?? 0) + (released.data?.total ?? 0)}
          active={state === 'ALL'}
          onClick={() => setF({ state: 'ALL' })}
        />
      </Counters>
      <BoardTable<QueueRow>
        caption="Pack lots for release"
        columns={columns}
        rows={list.data?.rows ?? []}
        getRowId={(r) => r.id}
        onRowClick={(r) => navigate(`/c/quality/release/${r.id}`)}
        loading={list.isLoading}
        error={list.error ? errorMessage(list.error) : undefined}
        empty={state === 'WAITING' ? 'No pack lot is waiting for release.' : 'Nothing here.'}
        pager={
          list.data
            ? {
                page: f.page,
                total: list.data.total,
                limit: list.data.limit,
                onPage: (page) => setF({ page }),
              }
            : undefined
        }
      />
      <Note>
        Until release, the pouches stay at the hall and cannot be sold or dispatched. The person who
        ran or cleaned the line never signs.
      </Note>
    </BoardPage>
  )
}

/**
 * Release · <pack lot> (alignment map V1-F7-06a Release, V1-F7-06b ReleaseBlocked; F7.6): the
 * named quality person reads the automatic checklist and the lab PDF, ticks, enters the password
 * and signs Release or Hold. Release is disabled while any line is red or waiting; the server
 * checks every line again when he signs.
 */
export function ReleasePage() {
  const { id } = useParams()
  const q = usePackLot(id)
  return (
    <QueryState query={q} label="the pack lot">
      {q.data && <Release pl={q.data} />}
    </QueryState>
  )
}

function Release({ pl }: { pl: PackLotDetail }) {
  const { user } = useAuth()
  const checklist = useChecklist(pl.id)
  const tree = useRecallExport(pl.code)
  const decide = useReleaseDecision()
  const [attested, setAttested] = useState(false)
  const [password, setPassword] = useState('')
  const [reason, setReason] = useState('')
  const lines = checklist.data ? releaseLines(checklist.data) : []
  const notGreen = linesNotGreen(lines)
  const ranTheLine = Boolean(
    user && (user.id === pl.operatorId || user.id === pl.cleanoutById) && !canOverrideTwoPerson(user.roles),
  )
  const ctx = {
    canRelease: checklist.data?.canRelease ?? false,
    ranTheLine,
    status: pl.status,
    hold: pl.hold,
  }
  const releaseBlocked = checklist.isLoading
    ? 'Reading the checklist…'
    : signBlocked('RELEASE', { attested, password, reason }, ctx)
  const holdBlocked = signBlocked('HOLD', { attested, password, reason }, ctx)
  const lot = tree.data?.lots[0]
  const farmer = lot ? tree.data?.farmers.find((f) => f.farmerId === lot.farmerId) : undefined
  const canSign = ctx.canRelease && !ranTheLine

  const sign = async (decision: 'RELEASE' | 'HOLD') => {
    try {
      await decide.mutateAsync({
        packLotId: pl.id,
        decision,
        reason: reason.trim() || undefined,
        attested,
        password,
      })
      setPassword('')
      setAttested(false)
    } catch {
      setPassword('')
    }
  }

  return (
    <BoardPage
      title={`Release · ${pl.code}`}
      actions={
        <LinkButton to="/c/quality/release" icon={<ArrowLeft aria-hidden="true" />}>
          Release list
        </LinkButton>
      }
    >
      <div className="flex flex-col gap-1">
        {pl.status === 'CLOSED' && pl.hold === 'NONE' ? (
          checklist.data && !checklist.data.canRelease ? (
            <Pill tone="bad">Cannot be released yet</Pill>
          ) : (
            <Pill tone="wait">Waiting for release</Pill>
          )
        ) : (
          <PackLotPill status={pl.status} hold={pl.hold} />
        )}
        <span className="text-[15px]">
          {pl.skuName} · {pl.pouchCount ?? '—'} pouches · batch {pl.batchCode ?? '—'}
          {lot && (
            <>
              {' '}
              · farmer lot {lot.code}
              {farmer?.name
                ? ` (${farmer.name})`
                : farmer?.farmerCode
                  ? ` (${farmer.farmerCode})`
                  : ''}
            </>
          )}
        </span>
      </div>
      <BoardColumns
        main={
          <div className="overflow-hidden rounded-card border-[1.5px] border-rule bg-surface">
            {checklist.isLoading && <p className="p-4 text-muted">Reading the checklist…</p>}
            {checklist.error && <p className="p-4 text-danger">{errorMessage(checklist.error)}</p>}
            {lines.map((l) => (
              <Line
                key={l.key}
                line={l}
                pl={pl}
                labReportIds={checklist.data?.labReportIds ?? []}
              />
            ))}
            {checklist.data?.computedAt && (
              <p className="px-4 py-2 text-[13px] text-muted">
                Checked {boardWhen(checklist.data.computedAt)} by the server.
              </p>
            )}
          </div>
        }
        side={
          <SidePanel label="Your decision" title="Your decision">
            <Signer>
              {ranTheLine ? (
                <Pill tone="bad" wrap>
                  You ran or cleaned this line
                </Pill>
              ) : notGreen > 0 ? (
                <Pill tone="bad">
                  {notGreen} line{notGreen === 1 ? ' is' : 's are'} not green
                </Pill>
              ) : (
                <Pill tone="ok">You did not run or clean this line</Pill>
              )}
            </Signer>
            <SignFields
              attested={attested}
              onAttested={setAttested}
              attestLabel="I have read the lab report and checked the label"
              password={password}
              onPassword={setPassword}
            />
            {!canSign && (
              <label className="flex flex-col gap-1 text-[15px]">
                <span className="font-bold">Reason (needed for Hold)</span>
                <TextArea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
              </label>
            )}
            <div className="flex-1" />
            <PanelActions>
              <ActionButton
                perm="pack-lots:release"
                leadingIcon={<SealCheck />}
                blockedReason={releaseBlocked}
                loading={decide.isPending && decide.variables?.decision === 'RELEASE'}
                onClick={() => void sign('RELEASE')}
              >
                Sign and release
              </ActionButton>
              {!ctx.canRelease && (
                <Note>Disabled while any line is red or waiting. Only Hold is possible.</Note>
              )}
              <ActionButton
                perm="pack-lots:release"
                variant="danger"
                leadingIcon={<HandPalm />}
                blockedReason={holdBlocked}
                loading={decide.isPending && decide.variables?.decision === 'HOLD'}
                onClick={() => void sign('HOLD')}
              >
                Hold
              </ActionButton>
              {canSign && (
                <details className="text-[14px]">
                  <summary className="cursor-pointer text-muted">
                    Hold instead: write a reason
                  </summary>
                  <TextArea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
                </details>
              )}
              <Note>
                The server checks every line again when you sign. Until release, the pouches stay at
                the hall and cannot be sold or dispatched. A hold needs a reason of at least{' '}
                {HOLD_REASON_MIN} letters.
              </Note>
            </PanelActions>
          </SidePanel>
        }
      />
    </BoardPage>
  )
}

const OK_TEXT: Record<string, string> = {
  lab_pass: 'Pass on every required panel; the label’s claim is backed and the result is recent.',
  label_ok: 'Label version approved. First pouch QR scanned and matched the lot link.',
  count_ok: 'Pouches and codes add up.',
  upstream_balanced:
    'No surplus in this pack run or the machine runs before it; packed brand kilos not above what the farmer delivered.',
  storage_declared:
    'Every sack had a known godown from the gate to the hopper; each godown’s signed no-fumigant declaration covers its time there.',
  upstream_clear: 'No hold on the farmer lot, the machine run or the batch.',
  exceptions_closed: 'None open.',
}

function Line({
  line,
  pl,
  labReportIds,
}: {
  line: BoardLine
  pl: PackLotDetail
  labReportIds: string[]
}) {
  const icon = line.tone === 'ok' ? 'ok' : line.tone === 'wait' ? 'wait' : 'bad'
  return (
    <div className="flex flex-col gap-1 border-b border-rule px-4 py-3 sm:flex-row sm:items-start sm:gap-4">
      <div className="w-40 shrink-0">
        <Pill tone={icon}>{line.title}</Pill>
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1 text-[15px]">
        {line.key === 'counts' || line.key === 'count_ok' ? (
          <span>
            {pl.pouchCount ?? '—'} pouches.{line.tone === 'ok' ? '' : ` ${line.detail.join('. ')}`}
          </span>
        ) : line.key === 'exceptions_closed' && line.tone !== 'ok' ? (
          <>
            {line.blocks && line.blocks.length > 0 && (
              <span className="text-danger">Blocks: {line.blocks.join(', ')}.</span>
            )}
            {line.listed && line.listed.length > 0 && (
              <span className="text-muted">
                Listed, not blocking in V1: {line.listed.join(', ')}.
              </span>
            )}
          </>
        ) : line.tone === 'ok' ? (
          <span>
            {line.key === 'label_ok'
              ? `${pl.labelCode}, approved. First pouch QR scanned and matched.`
              : OK_TEXT[line.key]}
          </span>
        ) : (
          line.detail.map((d) => <span key={d}>{d}</span>)
        )}
        {line.key === 'lab_pass' && labReportIds.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {labReportIds.map((id) => (
              <ReportLink key={id} id={id} />
            ))}
          </div>
        )}
        {line.key === 'upstream_balanced' && pl.packedG !== null && (
          <span className="text-[13px] text-muted">Packed here: {kg2(pl.packedG)}.</span>
        )}
        {line.key === 'exceptions_closed' && line.tone !== 'ok' && pl.batchUnitId && (
          <LinkButton to={`/c/exceptions?status=OPEN&unitId=${pl.batchUnitId}`}>
            Open problem
          </LinkButton>
        )}
        {line.key === 'count_ok' && (
          <span>
            <LinkButton to={`/c/packing/lots/${pl.id}`}>Run record</LinkButton>
          </span>
        )}
      </div>
    </div>
  )
}

function ReportLink({ id }: { id: string }) {
  const q = useLabReport(id)
  if (!q.data) return <span className="text-[13px] text-muted">Loading report…</span>
  return q.data.pdfUrl ? (
    <a
      href={q.data.pdfUrl}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1.5 rounded-chip border-[1.5px] border-[#8a827a] px-2.5 py-1 text-[14px] font-bold hover:bg-paper-tint"
    >
      <FilePdf aria-hidden="true" /> {q.data.reportNo}.pdf · {q.data.results.length} rows
    </a>
  ) : (
    <span className="text-[14px]">
      {q.data.reportNo} · {q.data.results.length} rows
    </span>
  )
}
